import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export type ImageInputValidationError = 'dimensions' | 'maxSize' | 'mime';

export interface ImageInputValidation {
  maxSizeKB?: number;
  mimeTypes?: string[];
  maxHeight?: number;
  maxWidth?: number;
  minHeight?: number;
  minWidth?: number;
  width?: number;
  height?: number;
}

@Component({
  selector: 'app-image-input',
  templateUrl: './image-input.component.html',
  styleUrls: ['./image-input.component.scss'],
  standalone: false
})
export class ImageInputComponent implements OnChanges, OnDestroy {
  @Input() accept = 'image/png,image/jpg,image/jpeg';
  @Input() iconClass = 'fa-regular fa-cloud-arrow-up';
  @Input() label = '';
  @Input() previewAlt = '';
  @Input() removeLabel = '';
  @Input() validation: ImageInputValidation = {};
  @Input() valueUrl: string | null = null;
  @Output() fileChange = new EventEmitter<File>();
  @Output() remove = new EventEmitter<void>();
  @Output() validationError = new EventEmitter<ImageInputValidationError>();

  displayUrl: string | null = null;
  private objectUrl: string | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.valueUrl && !this.objectUrl) {
      this.displayUrl = this.valueUrl || null;
    }
  }

  ngOnDestroy() {
    this.revokeObjectUrl();
  }

  selectFileFromInput(event: Event) {
    const input = event.target as HTMLInputElement;

    this.selectFile(input.files?.[0]);
    input.value = '';
  }

  selectFile(file?: File) {
    if (!file) {
      return;
    }

    this.validateFile(file).subscribe(error => {
      if (error) {
        this.validationError.emit(error);
        return;
      }

      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(file);
      this.displayUrl = this.objectUrl;
      this.fileChange.emit(file);
    });
  }

  removeImage() {
    this.revokeObjectUrl();
    this.displayUrl = null;
    this.remove.emit();
  }

  private validateFile(file: File): Observable<ImageInputValidationError | null> {
    if (this.validation?.mimeTypes?.length && !this.validation.mimeTypes.includes(file.type)) {
      return of<ImageInputValidationError>('mime');
    }

    if (this.validation?.maxSizeKB && file.size > this.validation.maxSizeKB * 1024) {
      return of<ImageInputValidationError>('maxSize');
    }

    if (!this.hasDimensionValidation()) {
      return of(null);
    }

    return this.getImageDimensions(file).pipe(
      map((dimensions): ImageInputValidationError | null => {
        const isValidWidth = this.isDimensionValid(dimensions.width, this.validation.width, this.validation.minWidth, this.validation.maxWidth);
        const isValidHeight = this.isDimensionValid(dimensions.height, this.validation.height, this.validation.minHeight, this.validation.maxHeight);

        return isValidWidth && isValidHeight ? null : 'dimensions';
      }),
      catchError(() => of<ImageInputValidationError>('dimensions'))
    );
  }

  private getImageDimensions(file: File): Observable<{ width: number; height: number }> {
    return new Observable(subscriber => {
      const image = new Image();
      let objectUrl = URL.createObjectURL(file);
      const cleanUp = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };

      image.onload = () => {
        cleanUp();
        subscriber.next({ width: image.naturalWidth, height: image.naturalHeight });
        subscriber.complete();
      };
      image.onerror = () => {
        cleanUp();
        subscriber.error();
      };
      image.src = objectUrl;

      return cleanUp;
    });
  }

  private hasDimensionValidation(): boolean {
    return !!(this.validation?.width || this.validation?.height || this.validation?.minWidth || this.validation?.minHeight || this.validation?.maxWidth || this.validation?.maxHeight);
  }

  private isDimensionValid(value: number, exact?: number, min?: number, max?: number): boolean {
    if (exact && value !== exact) {
      return false;
    }

    if (min && value < min) {
      return false;
    }

    return !max || value <= max;
  }

  private revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
